package top.wcpe.allincore.dsl.compiler.semantic

import top.wcpe.allincore.dsl.compiler.api.ProjectTypeTable
import top.wcpe.allincore.dsl.compiler.ast.ScreenAst
import top.wcpe.allincore.dsl.compiler.diag.DiagnosticCollector
import top.wcpe.allincore.dsl.compiler.pipeline.PlaceholderRegistry

/**
 * 语义检查入口。
 *
 * 流程：
 * 1. [ScopeBuilder] 收集 state/action 符号并报告重复声明；
 * 2. [ReferenceResolver] 检查 state 引用、action 调用、onClick 绑定；
 * 3. [ElementValidator] 校验元素 kind 白名单 / id 命名 / 同级 id 唯一。
 *
 * 各 Phase 内部均不抛异常，错误统一通过 [DiagnosticCollector] 收集。
 *
 * @property knownComponentNames IDE 单文件分析下的"项目级已知 component 名"白名单，
 *   透传给 [ElementValidator]。详见 [top.wcpe.allincore.dsl.compiler.pipeline.CompileOptions.knownComponentNames]。
 * @property crossFileTypes M10 Stage 4：跨文件类型表，透传给 [TypeConstraintChecker]
 *   驱动 E669（data 字段访问名不存在）等基于跨文件 data 字段名的诊断。默认空表，
 *   向后兼容旧调用方。
 */
class SemanticChecker(
    private val diag: DiagnosticCollector,
    private val placeholderRegistry: PlaceholderRegistry? = null,
    private val knownComponentNames: Set<String> = emptySet(),
    private val crossFileTypes: ProjectTypeTable = ProjectTypeTable.EMPTY,
    /** 已知 theme token 白名单（screen 合并 token ∪ 全部 scheme token）；为空时跳过 theme.x 校验。 */
    private val knownThemeTokens: Set<String> = emptySet(),
    /** 主题 token 严格校验开关；详见 [CompileOptions.strictThemeTokens]。 */
    private val strictThemeTokens: Boolean = false,
) {
    fun check(screen: ScreenAst) {
        val scope = ScopeBuilder(diag).build(screen)
        ReferenceResolver(diag, scope, knownThemeTokens, strictThemeTokens).resolve(screen)
        ElementValidator(diag, knownComponentNames).validate(screen.root)
        ModelPreviewChecker(diag).check(screen)
        AwaitPositionChecker(diag).check(screen)
        SuspendLambdaUsageChecker(diag).check(screen)
        // I5：state 显式类型标注与默认字面量的一致性校验
        StateTypeChecker(diag).check(screen)
        // I4：screen 级 fun 调用的 arity / 命名实参校验
        CallArityChecker(diag).check(screen)
        // C1：var 显式类型标注与初始化值的一致性约束（E620 / E621）
        TypeConstraintChecker(diag, crossFileTypes).check(screen)
        // M5-e：suspend 关键字强制显式校验（E625 / E626）
        SuspendKeywordChecker(diag).check(screen)
        // M6-a：data 类型 + Map 字面量一致性校验（E748/E749/E751/E756/W730）
        // M10 Stage 8：透传 crossFileTypes 让 E748 同样识别跨文件 data 类型。
        DataLiteralChecker(diag, crossFileTypes).check(screen)
        // Stage 3：class 内 fun 作用域守卫骨架（E790 占位）；Stage 6 接入语法后生效
        ClassBodyChecker(diag).check(screen)
        // M6-b：容器形参 + source 属性校验（E702/E703/E704/E705/E706/W707）
        SourceContainerChecker(diag).check(screen)
        // Stage 1：close 块同步限制校验（E640/E641/E642/E643/E644/E645）
        CloseBlockChecker(diag).check(screen)
        // Stage4：PAPI 占位符编译期基础语义校验
        PapiChecker(diag, placeholderRegistry).check(screen)
        // Stage B：subscribe 订阅块语义校验
        SubscribeChecker(diag).check(screen)
        // derived 派生值校验：禁止 await + 循环依赖检测（E204/E205）
        DerivedExprChecker(diag).check(screen)
    }
}
